import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useBio } from '../../contexts/BioContext';
import { useTranslations } from '../../contexts/TranslationsContext';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import MailIcon from '@mui/icons-material/Mail';
import { scroller } from 'react-scroll';
import {
  Nav,
  NavLink,
  NavbarContainer,
  Span,
  NavLogo,
  NavItems,
  GitHubButton,
  ThemeButton,
  ButtonContainer,
  ThemeToggleWrapper,
  MobileIcon,
  MobileMenu,
  MobileLink,
  ProgressBar,
  LanguageDropdown,
  LanguageButton,
  MobileLanguageButton,
} from './NavbarStyles';
import { DiCssdeck } from 'react-icons/di';
import { FaBars, FaSun, FaTimes } from 'react-icons/fa';
import { RiMoonClearFill } from 'react-icons/ri';
import { ThemeContext } from '../../contexts/ThemeContext';
import InfoIcon from '@mui/icons-material/Info';
import CodeIcon from '@mui/icons-material/Code';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SchoolIcon from '@mui/icons-material/School';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { CircleFlag } from 'react-circle-flags';
import { setLanguage, getLanguage } from '../../firebase';

const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

const SECTIONS = [
  { name: 'about' },
  { name: 'skills' },
  { name: 'experience' },
  { name: 'projects' },
  { name: 'education' },
  { name: 'testimonials' },
];

const SECTION_ICONS = {
  about: InfoIcon,
  skills: CodeIcon,
  experience: BusinessCenterIcon,
  projects: RocketLaunchIcon,
  education: SchoolIcon,
  testimonials: RateReviewIcon,
};

const Navbar = React.memo(() => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const {
    theme: themeString,
    toggleTheme,
    themeObject,
  } = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const { bio } = useBio();
  const { translations } = useTranslations();
  const language = getLanguage();
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const desktopLangRef = useRef();
  const mobileLangRef = useRef();
  const mobileDropdownRef = useRef();

  const githubUrl = useMemo(() => bio?.github || '', [bio]);
  const linkedinUrl = useMemo(() => bio?.linkedin || '', [bio]);

  useEffect(() => {
    const handleLanguageChange = () => {
      // Force re-render when language changes
      setDesktopDropdownOpen(false);
      setMobileDropdownOpen(false);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () =>
      window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setMobileDropdownOpen(false);
    setDesktopDropdownOpen(false);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
      });
    }, 400);
  };

  const handleThemeChange = useCallback(() => toggleTheme(), [toggleTheme]);

  const handleLogoClick = useCallback((e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const updateScrollProgress = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollableHeight = documentHeight - windowHeight;

      if (scrollableHeight > 0) {
        const progress = Math.min((scrollTop / scrollableHeight) * 100, 100);
        setScrollPosition(progress);
      } else {
        setScrollPosition(0);
      }
    };

    const handleScroll = throttle(() => {
      updateScrollProgress();
    }, 16);

    const handleResize = throttle(() => {
      updateScrollProgress();
    }, 100);

    updateScrollProgress();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopLangRef.current &&
        !desktopLangRef.current.contains(event.target)
      ) {
        setDesktopDropdownOpen(false);
      }
      if (mobileLangRef.current) {
        const isClickInsideButton = mobileLangRef.current.contains(
          event.target
        );
        const isClickInsideDropdown =
          mobileDropdownRef.current &&
          mobileDropdownRef.current.contains(event.target);
        if (!isClickInsideButton && !isClickInsideDropdown) {
          setMobileDropdownOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleScrollLink = (e, section) => {
    e.preventDefault();
    scroller.scrollTo(section, { smooth: true, duration: 1100 });
    setIsOpen(false);
  };

  return (
    <Nav>
      <NavbarContainer>
        <NavLogo as="div">
          <div
            onClick={handleLogoClick}
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <DiCssdeck
              size="2.3rem"
              style={{ color: themeString === 'dark' ? 'white' : 'black' }}
            />
            <Span>{translations?.nav?.portfolio || 'Portfolio'}</Span>
          </div>
        </NavLogo>

        <ThemeToggleWrapper $language={language} onClick={handleThemeChange}>
          {themeString === 'dark' ? (
            <RiMoonClearFill size="1.35rem" />
          ) : (
            <FaSun size="1.35rem" />
          )}
        </ThemeToggleWrapper>

        <NavItems>
          {SECTIONS.map((section) => {
            const sectionKey =
              section.name === 'about' ? 'about' : section.name;
            const sectionLabel =
              translations?.nav?.[sectionKey] ||
              section.name.charAt(0).toUpperCase() + section.name.slice(1);
            return (
              <li key={section.name}>
                <NavLink
                  href={`#${section.name}`}
                  onClick={(e) => handleScrollLink(e, section.name)}
                  $language={language}
                >
                  {sectionLabel}
                </NavLink>
              </li>
            );
          })}
        </NavItems>

        <ButtonContainer>
          <GitHubButton>
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GitHubIcon style={{ fontSize: 22 }} />
            </a>
          </GitHubButton>

          <ThemeButton onClick={handleThemeChange}>
            {themeString === 'dark' ? (
              <RiMoonClearFill size="1.1rem" />
            ) : (
              <FaSun size="1.1rem" />
            )}
          </ThemeButton>

          <LanguageDropdown ref={desktopLangRef}>
            <LanguageButton
              onClick={() => setDesktopDropdownOpen(!desktopDropdownOpen)}
              style={{
                border: `1.8px solid ${themeObject.primary}`,
                borderRadius: '15px',
                padding: '0',
                width: '40px',
                height: '40px',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.4s ease-in-out',
              }}
            >
              {language === 'en' ? (
                <CircleFlag countryCode="gb" height={28} width={28} />
              ) : (
                <CircleFlag countryCode="sa" height={28} width={28} />
              )}
            </LanguageButton>

            {desktopDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '50px',
                  ...(language === 'ar' ? { left: 0 } : { right: 0 }),
                  background:
                    themeObject.card_light || themeObject.card || '#ffffff',
                  border: `2px solid ${themeObject.primary}`,
                  borderRadius: '20px',
                  padding: '12px 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  minWidth: '140px',
                  boxShadow: `0 8px 24px rgba(0, 0, 0, ${themeString === 'dark' ? '0.4' : '0.15'})`,
                  zIndex: 'var(--z-dropdown)',
                  animation: 'slideDown 0.3s ease-out',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    background: 'transparent',
                    border: `1.5px solid ${themeObject.primary}`,
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }}
                >
                  {language === 'en' ? (
                    <>
                      <CircleFlag
                        countryCode="gb"
                        height={24}
                        width={24}
                        style={{ opacity: 0.5 }}
                      />
                      <span
                        style={{
                          marginLeft: '12px',
                          fontSize: '15px',
                          fontWeight: 600,
                          color: themeObject.text_primary,
                        }}
                      >
                        {translations?.languages?.english || 'English'}
                      </span>
                    </>
                  ) : (
                    <>
                      <CircleFlag
                        countryCode="sa"
                        height={24}
                        width={24}
                        style={{ opacity: 0.5 }}
                      />
                      <span
                        style={{
                          marginRight: '12px',
                          fontSize: '15px',
                          fontWeight: 600,
                          color: themeObject.text_primary,
                        }}
                      >
                        {translations?.languages?.arabic || 'العربية'}
                      </span>
                    </>
                  )}
                </div>

                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageChange(language === 'en' ? 'ar' : 'en');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    background: 'transparent',
                    border: `2px solid ${themeObject.primary}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: '700',
                    userSelect: 'none !important',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = themeObject.primary + '15';
                    e.target.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.opacity = '1';
                  }}
                >
                  {language === 'en' ? (
                    <>
                      <CircleFlag countryCode="sa" height={24} width={24} />
                      <span
                        style={{
                          marginLeft: '12px',
                          fontSize: '15px',
                          fontWeight: 700,
                          color: themeObject.text_primary,
                        }}
                      >
                        {translations?.languages?.arabic || 'العربية'}
                      </span>
                    </>
                  ) : (
                    <>
                      <CircleFlag countryCode="gb" height={24} width={24} />
                      <span
                        style={{
                          marginRight: '12px',
                          fontSize: '15px',
                          fontWeight: 700,
                          color: themeObject.text_primary,
                        }}
                      >
                        {translations?.languages?.english || 'English'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </LanguageDropdown>
        </ButtonContainer>

        <MobileLanguageButton
          $language={language}
          ref={mobileLangRef}
          onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
        >
          {language === 'en' ? (
            <CircleFlag countryCode="gb" height={28} width={28} />
          ) : (
            <CircleFlag countryCode="sa" height={28} width={28} />
          )}
        </MobileLanguageButton>

        {mobileDropdownOpen && (
          <div
            ref={mobileDropdownRef}
            style={{
              position: 'absolute',
              top: '65px',
              ...(language === 'ar'
                ? { left: '1.5rem', right: 'auto' }
                : { right: '1.5rem', left: 'auto' }),
              background:
                themeObject.card_light || themeObject.card || '#ffffff',
              border: `2px solid ${themeObject.primary}`,
              borderRadius: '20px',
              padding: '12px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              minWidth: '140px',
              boxShadow: `0 8px 24px rgba(0, 0, 0, ${themeString === 'dark' ? '0.4' : '0.15'})`,
              zIndex: 'var(--z-tooltip)',
              animation: 'slideDown 0.3s ease-out',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: '12px',
                background: 'transparent',
                border: `1.5px solid ${themeObject.primary}`,
                opacity: 0.5,
                cursor: 'not-allowed',
              }}
            >
              {language === 'en' ? (
                <>
                  <CircleFlag
                    countryCode="gb"
                    height={24}
                    width={24}
                    style={{ opacity: 0.5 }}
                  />
                  <span
                    style={{
                      marginLeft: '12px',
                      fontSize: '15px',
                      fontWeight: 600,
                      color: themeObject.text_primary,
                    }}
                  >
                    {translations?.languages?.english || 'English'}
                  </span>
                </>
              ) : (
                <>
                  <CircleFlag
                    countryCode="sa"
                    height={24}
                    width={24}
                    style={{ opacity: 0.5 }}
                  />
                  <span
                    style={{
                      marginRight: '12px',
                      fontSize: '15px',
                      fontWeight: 600,
                      color: themeObject.text_primary,
                    }}
                  >
                    {translations?.languages?.arabic || 'العربية'}
                  </span>
                </>
              )}
            </div>

            <div
              onClick={(e) => {
                e.stopPropagation();
                handleLanguageChange(language === 'en' ? 'ar' : 'en');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: '12px',
                background: 'transparent',
                border: `2px solid ${themeObject.primary}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontWeight: '700',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = themeObject.primary + '15';
                e.target.style.opacity = '1';
                e.target.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.opacity = '1';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {language === 'en' ? (
                <>
                  <CircleFlag countryCode="sa" height={24} width={24} />
                  <span
                    style={{
                      marginLeft: '12px',
                      fontSize: '15px',
                      fontWeight: 700,
                      color: themeObject.text_primary,
                    }}
                  >
                    {translations?.languages?.arabic || 'العربية'}
                  </span>
                </>
              ) : (
                <>
                  <CircleFlag countryCode="gb" height={24} width={24} />
                  <span
                    style={{
                      marginRight: '12px',
                      fontSize: '15px',
                      fontWeight: 700,
                      color: themeObject.text_primary,
                    }}
                  >
                    {translations?.languages?.english || 'English'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        <MobileIcon $language={language}>
          {isOpen ? (
            <FaTimes onClick={() => setIsOpen(!isOpen)} />
          ) : (
            <FaBars onClick={() => setIsOpen(!isOpen)} />
          )}
        </MobileIcon>

        <MobileMenu isOpen={isOpen} $language={language}>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {SECTIONS.map((section) => {
              const SectionIcon = SECTION_ICONS[section.name];
              return (
                <li key={section.name}>
                  <MobileLink
                    href={`#${section.name}`}
                    onClick={(e) => handleScrollLink(e, section.name)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <SectionIcon
                        style={{
                          fontSize: '22px',
                          marginLeft: language === 'ar' ? '15px' : 0,
                          marginRight: language === 'ar' ? 0 : '15px',
                          color: themeObject.text_primary,
                          verticalAlign: 'middle',
                        }}
                      />
                      {translations?.nav?.[section.name] ||
                        section.name.charAt(0).toUpperCase() +
                          section.name.slice(1)}
                    </div>
                  </MobileLink>
                </li>
              );
            })}
          </ul>

          <MobileLink>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                marginTop: '0.5rem',
                marginBottom: '0.5rem',
              }}
            >
              <NavLink
                href={linkedinUrl}
                target="_blank"
                aria-label="LinkedIn"
                rel="noopener noreferrer"
                $language={language}
                $noUnderline
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '14px 8px',
                  borderRadius: '16px',
                  background:
                    themeString === 'dark'
                      ? 'rgba(5, 150, 105, 0.1)'
                      : 'rgba(5, 150, 105, 0.05)',
                  border: `1px solid ${themeObject.primary}`,
                  textDecoration: 'none',
                  color: themeObject.text_primary,
                  transition: 'all 0.3s ease',
                  fontSize: '11px',
                  fontWeight: '500',
                }}
              >
                <LinkedInIcon
                  style={{
                    fontSize: '1.8rem',
                    color: themeObject.text_primary,
                  }}
                />
                <span>{translations?.social?.linkedin || 'LinkedIn'}</span>
              </NavLink>
              <NavLink
                href={githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                $language={language}
                $noUnderline
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '14px 8px',
                  borderRadius: '16px',
                  background:
                    themeString === 'dark'
                      ? 'rgba(5, 150, 105, 0.1)'
                      : 'rgba(5, 150, 105, 0.05)',
                  border: `1px solid ${themeObject.primary}`,
                  textDecoration: 'none',
                  color: themeObject.text_primary,
                  transition: 'all 0.3s ease',
                  fontSize: '11px',
                  fontWeight: '500',
                }}
              >
                <GitHubIcon
                  style={{
                    fontSize: '1.8rem',
                    color: themeObject.text_primary,
                  }}
                />
                <span>{translations?.social?.github || 'GitHub'}</span>
              </NavLink>
              <NavLink
                href="mailto:farhatrkhalil@gmail.com"
                aria-label="Email"
                $language={language}
                $noUnderline
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '14px 8px',
                  borderRadius: '16px',
                  background:
                    themeString === 'dark'
                      ? 'rgba(5, 150, 105, 0.1)'
                      : 'rgba(5, 150, 105, 0.05)',
                  border: `1px solid ${themeObject.primary}`,
                  textDecoration: 'none',
                  color: themeObject.text_primary,
                  transition: 'all 0.3s ease',
                  fontSize: '11px',
                  fontWeight: '500',
                }}
              >
                <MailIcon
                  style={{
                    fontSize: '1.8rem',
                    color: themeObject.text_primary,
                  }}
                />
                <span>{translations?.social?.email || 'Email'}</span>
              </NavLink>
            </div>
          </MobileLink>
        </MobileMenu>
      </NavbarContainer>

      <ProgressBar progress={scrollPosition} $language={language} />
    </Nav>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;
